/**
 * Browser-side logger.
 *
 * - Persists a sessionId in sessionStorage so all events in one browser
 *   session can be correlated in CloudWatch.
 * - Batches log entries and sends them to POST /api/log every 2 s (or
 *   immediately on error / full buffer).
 * - Always mirrors to the browser console so DevTools still work.
 */

const SESSION_KEY = 'mra_session_id'
const BASE = import.meta.env.VITE_API_URL ?? '/api'
const FLUSH_INTERVAL_MS = 2000
const MAX_QUEUE = 15

// ── Session ID ────────────────────────────────────────────────────────────────

function makeId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

function resolveSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = makeId()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return makeId()
  }
}

export const sessionId: string = resolveSessionId()

// ── Caller location ───────────────────────────────────────────────────────────

/**
 * Walk the Error stack to find the first frame outside logger.ts.
 * In Vite dev mode the stack already uses original TypeScript paths.
 * Returns e.g. "api.ts:195" or "" if unparseable.
 */
function callerLocation(): string {
  const stack = new Error().stack ?? ''
  for (const line of stack.split('\n').slice(1)) {
    if (/logger\.[jt]s/.test(line)) continue
    const m = line.match(/([^/\\()\s]+\.[jt]sx?):(\d+)/)
    if (m) return `${m[1]}:${m[2]}`
  }
  return ''
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogEntry {
  level: LogLevel
  event: string
  data: Record<string, unknown>
  ts: number
  session_id: string
  caller?: string
}

// ── Queue & flush ─────────────────────────────────────────────────────────────

const _queue: LogEntry[] = []
let _timer: ReturnType<typeof setTimeout> | null = null

function flush(): void {
  if (_queue.length === 0) return
  const batch = _queue.splice(0)
  // Use sendBeacon so logs survive page unload; fall back to fetch
  const body = JSON.stringify({ logs: batch })
  const url = `${BASE}/log`
  try {
    if (!navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {})
    }
  } catch {
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {})
  }
}

function scheduleFlush(): void {
  if (!_timer) {
    _timer = setTimeout(() => {
      _timer = null
      flush()
    }, FLUSH_INTERVAL_MS)
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export function log(level: LogLevel, event: string, data: Record<string, unknown> = {}): void {
  const caller = callerLocation()


  // Mirror to browser console — caller shown as a suffix so DevTools can hyperlink it
  const consoleFn =
    level === 'error' ? console.error
    : level === 'warn' ? console.warn
    : level === 'debug' ? console.debug
    : console.log
  const label = caller ? `[${event}] ${caller}` : `[${event}]`
  consoleFn(label, Object.keys(data).length ? data : '')

  // Only send info/warn/error to the backend — debug is console-only
  if (level === 'debug') return

  const entry: LogEntry = { level, event, data, ts: Date.now(), session_id: sessionId, caller }
  _queue.push(entry)

  // Flush immediately on error or full buffer
  if (level === 'error' || _queue.length >= MAX_QUEUE) {
    if (_timer) { clearTimeout(_timer); _timer = null }
    flush()
  } else {
    scheduleFlush()
  }
}

// Convenience shorthands
export const debug = (event: string, data?: Record<string, unknown>) => log('debug', event, data)
export const info  = (event: string, data?: Record<string, unknown>) => log('info',  event, data)
export const warn  = (event: string, data?: Record<string, unknown>) => log('warn',  event, data)
export const error = (event: string, data?: Record<string, unknown>) => log('error', event, data)

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush)
  window.addEventListener('pagehide', flush)
}
